import { stackOdds } from '@sportal365/smp-widget-utils/src/oddsCounter';
import { ConfigContext, OddsDisplay, SMPContainer, SMPText } from '@velinsi/smp-widgets-ui';
import { useCallback, useContext, useEffect, useState } from 'react';
import { Sports } from '../../../../../packages/constants/sportType';
import useAbortController from '../../../../../packages/hooks/useAbortController';
import useBasketballApi from '../../../../../packages/hooks/useBasketballApi';
import useFetchIntervalData from '../../../../../packages/hooks/useFetchIntervalData';
import useRootNodeRef from '../../../../../packages/hooks/useRootNodeRef';
import useSubscriptionClick from '../../../../../packages/hooks/useSubscriptionClick';
import useTennisApi from '../../../../../packages/hooks/useTennisApi';
import ThemeProvider from '../../../../../packages/theme/ThemeProvider';
import {
  BaseWidgetProps,
  IBasketballSingleEventResponseData,
  IFootballOddsConfig,
  IFootballSingleEventResponseData,
  IOddsV2,
  ITennisSingleEventResponseData,
} from '../../../../../packages/types';
import FootballApiContext from '../../contexts/FootballApiContext';
import useReconcileScopeMarkets from '../../hooks/useReconcileScopeMarkets';
import useOverlapOdds from '../../../../../packages/hooks/useOverlapOdds';

export type OddsWidgetProps = BaseWidgetProps<IFootballOddsConfig>;

export const OddsWidget = ({ themes, config, subscribers, sdkOptions, shadow, cuid }: OddsWidgetProps) => {
  const [odds, setOdds] = useState<IOddsV2>();
  const [isLoading, setIsLoading] = useState(false);
  const { $football } = useContext(FootballApiContext);
  const { $tennis } = useTennisApi(sdkOptions);
  const { $basketball } = useBasketballApi(sdkOptions);
  const [getSignal] = useAbortController();
  const { ref } = useRootNodeRef();
  const { oddsMarket, oddsMarketScope } = useReconcileScopeMarkets(config?.dataOddsMarket);
  const { areBettingOddsOverlapVisible } = useOverlapOdds({ dataBettingLogoOverlap: config?.dataBettingLogoOverlap });

  const fetchOdds = useCallback(
    (manageLoader: boolean, isFromInterval = false) => {
      if (!config) {
        return;
      }

      const {
        dataMatchId,
        dataOddsBettingId,
        dataOddsPreEventOnly,
        dataOddsMarketValueType,
        dataWidgetSport,
        dataWidgetId,
        dataOddsScopeType,
      } = config;
      const signal = getSignal();

      manageLoader && setIsLoading(true);

      if (dataWidgetSport === Sports.FOOTBALL) {
        $football
          ?.getMatchById({
            signal,
            dataMatchId,
            dataOddsBettingId,
            dataOddsPreEventOnly,
            dataOddsDisplay: true,
            dataOddsMarketValueType,
            dataOddsMarket: oddsMarket,
            dataOddsScopeType,
          })
          ?.then((response: IFootballSingleEventResponseData) => {
            manageLoader && setIsLoading(false);
            setOdds(response.odds);
            !isFromInterval && stackOdds({ odds: response.odds }, dataWidgetSport, dataWidgetId, cuid, dataOddsBettingId, true);
          })
          .catch((error: unknown) => {
            console.error(error);
          });
      } else if (dataWidgetSport === Sports.TENNIS) {
        $tennis
          ?.getMatchById({
            dataMatchId,
            dataOddsMarket: oddsMarket,
            dataOddsMarketValueType,
            dataOddsDisplay: true,
            dataOddsBettingId,
            dataOddsPreEventOnly,
          })
          ?.then((match: ITennisSingleEventResponseData) => {
            manageLoader && setIsLoading(false);
            setOdds(match.match.odds);
            !isFromInterval && stackOdds(match, dataWidgetSport, dataWidgetId, cuid, dataOddsBettingId, true);
          })
          .catch((error: unknown) => {
            console.error(error);
          });
      } else if (dataWidgetSport === Sports.BASKETBALL) {
        $basketball
          ?.getGameById({
            dataMatchId,
            dataOddsMarket: oddsMarket,
            dataOddsMarketValueType,
            dataOddsDisplay: true,
            dataOddsPreEventOnly,
            dataOddsBettingId,
          })
          ?.then((game: IBasketballSingleEventResponseData) => {
            manageLoader && setIsLoading(false);
            setOdds(game.game.odds);
            !isFromInterval && stackOdds(game, dataWidgetSport, dataWidgetId, cuid, dataOddsBettingId, true);
          })
          .catch((error: unknown) => {
            console.error(error);
          });
      }
    },
    [$basketball, $football, $tennis, config, getSignal, cuid, oddsMarket],
  );

  const handleClick = useSubscriptionClick({
    dataWidgetId: config?.dataWidgetId,
    dataWidgetSport: config?.dataWidgetSport,
    subscribers,
    dataWidgetCuid: cuid,
  });

  useEffect(() => {
    fetchOdds(true);
  }, [fetchOdds]);

  useFetchIntervalData(fetchOdds, config?.dataRefreshTime);

  if (!config) {
    return null;
  }

  return (
    <ConfigContext.Provider value={{ ...config, sdkOptions }}>
      <ThemeProvider
        slickStyles={true}
        shadow={shadow}
        themes={themes}
        dataTheme={config?.dataTheme}
        isLoading={isLoading}
        refForShadow={ref}
      >
        <SMPContainer css={{ padding: '$3 0', backgroundColor: '$oddsContainerPrimaryBgColor' }}>
          {odds && odds.length > 0 ? (
            <OddsDisplay
              odds={odds}
              handleClick={handleClick}
              marketSelectionDisplay={!config.dataOddsMarket}
              isStandalone
              oddsMarketScope={oddsMarketScope}
              areBettingOddsOverlapVisible={areBettingOddsOverlapVisible}
            />
          ) : (
            <SMPText textAlign='center' variant='contrast'>
              {config.dataLabels?.no_odds ?? 'No odds available'}
            </SMPText>
          )}
        </SMPContainer>
      </ThemeProvider>
    </ConfigContext.Provider>
  );
};
